import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Search } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Card } from '@shared/ui/containers/Card';
import { ActionButton } from '@shared/ui/forms/FormFields';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { useDebounce } from '@shared/hooks/useDebounce';
import { cn } from '@shared/lib/cn';
import { toast } from '@shared/ui/feedback/Toast';
import {
  fetchRelationshipTypes,
  searchEstateProperties,
  submitPropertyClaim,
  uploadClaimDocument,
  type EstatePropertyOption,
  type PropertyRelationshipTypeOption,
  type PropertyResidencyKind,
  type RequiredClaimDocument,
} from './api';

type Step = 'property' | 'relationship' | 'documents';

const RESIDENCY_OPTIONS: Array<{ value: PropertyResidencyKind; label: string }> = [
  { value: 'resident', label: 'Resident' },
  { value: 'non_resident', label: 'Non-resident' },
  { value: 'temporary_resident', label: 'Temporary resident' },
];

interface ClaimPropertySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Swipeable bottom sheet: search estate properties, pick relationship /
 * residency, upload required documents per relationship config, submit claim.
 */
export function ClaimPropertySheet({ open, onOpenChange }: ClaimPropertySheetProps): JSX.Element {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('property');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedProperty, setSelectedProperty] = useState<EstatePropertyOption | null>(null);
  const [relationshipTypeId, setRelationshipTypeId] = useState('');
  const [residencyKind, setResidencyKind] = useState<PropertyResidencyKind>('resident');
  const [uploadedDocIds, setUploadedDocIds] = useState<Set<string>>(new Set());
  const [uploadingDocId, setUploadingDocId] = useState<string | null>(null);

  const relationshipQuery = useQuery({
    queryKey: ['property-relationship-types'],
    queryFn: fetchRelationshipTypes,
    enabled: open,
  });

  const propertiesQuery = useQuery({
    queryKey: ['estate-properties-search', debouncedSearch],
    queryFn: () => searchEstateProperties(debouncedSearch),
    enabled: open && step === 'property',
  });

  const relationshipTypes = relationshipQuery.data ?? [];
  const selectedRelationship = useMemo(
    () => relationshipTypes.find((r) => r.relationship_type_id === relationshipTypeId),
    [relationshipTypes, relationshipTypeId],
  );
  const requiredDocuments = selectedRelationship?.required_documents ?? [];

  useEffect(() => {
    if (!open) {
      setStep('property');
      setSearch('');
      setSelectedProperty(null);
      setRelationshipTypeId('');
      setResidencyKind('resident');
      setUploadedDocIds(new Set());
      setUploadingDocId(null);
    }
  }, [open]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProperty) throw new Error('Choose a property');
      if (!relationshipTypeId) throw new Error('Choose your relationship to the property');
      for (const doc of requiredDocuments) {
        if (!uploadedDocIds.has(doc.document_type_id)) {
          throw new Error(`Upload required: ${doc.name}`);
        }
      }
      return submitPropertyClaim({
        location_id: selectedProperty.location_id,
        property_relationship_type_id: relationshipTypeId,
        property_residency_kind: residencyKind,
      });
    },
    onSuccess: () => {
      toast.success('Claim submitted', 'The estate team will review your request.');
      void queryClient.invalidateQueries({ queryKey: ['my-property-claims'] });
      onOpenChange(false);
    },
    onError: (e) =>
      toast.danger("Couldn't submit claim", e instanceof Error ? e.message : undefined),
  });

  const handleSelectProperty = (property: EstatePropertyOption) => {
    setSelectedProperty(property);
    setStep('relationship');
  };

  const handleRelationshipNext = () => {
    if (!relationshipTypeId) {
      toast.danger('Relationship required', 'Select how you are linked to this property.');
      return;
    }
    if (requiredDocuments.length > 0) {
      setStep('documents');
    } else {
      submitMutation.mutate();
    }
  };

  const handleUpload = async (doc: RequiredClaimDocument, file: File | null) => {
    if (!file) return;
    setUploadingDocId(doc.document_type_id);
    try {
      await uploadClaimDocument(file, doc.document_type_id, {
        title: doc.name,
        relationshipTypeId,
        fileName: file.name,
      });
      setUploadedDocIds((prev) => new Set(prev).add(doc.document_type_id));
      toast.success('Uploaded', doc.name);
    } catch (e) {
      toast.danger('Upload failed', e instanceof Error ? e.message : undefined);
    } finally {
      setUploadingDocId(null);
    }
  };

  const sheetTitle =
    step === 'property'
      ? 'Find your property'
      : step === 'relationship'
        ? 'Your connection'
        : 'Supporting documents';

  const sheetDescription =
    step === 'property'
      ? 'Search by unit number, street or estate name.'
      : step === 'relationship'
        ? selectedProperty?.name
        : 'Upload each document required for your ownership type.';

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={sheetTitle}
      description={sheetDescription}
      height="min(92dvh, 640px)"
    >
      {step === 'property' ? (
        <PropertySearchStep
          search={search}
          onSearchChange={setSearch}
          loading={propertiesQuery.isLoading}
          properties={propertiesQuery.data?.properties ?? []}
          onSelect={handleSelectProperty}
        />
      ) : null}

      {step === 'relationship' ? (
        <RelationshipStep
          relationshipTypes={relationshipTypes}
          loading={relationshipQuery.isLoading}
          relationshipTypeId={relationshipTypeId}
          onRelationshipTypeId={setRelationshipTypeId}
          residencyKind={residencyKind}
          onResidencyKind={setResidencyKind}
          onBack={() => setStep('property')}
          onNext={handleRelationshipNext}
          submitting={submitMutation.isPending}
          skipDocuments={requiredDocuments.length === 0}
        />
      ) : null}

      {step === 'documents' ? (
        <DocumentsStep
          documents={requiredDocuments}
          uploadedDocIds={uploadedDocIds}
          uploadingDocId={uploadingDocId}
          onUpload={handleUpload}
          onBack={() => setStep('relationship')}
          onSubmit={() => submitMutation.mutate()}
          submitting={submitMutation.isPending}
        />
      ) : null}
    </BottomSheet>
  );
}

function PropertySearchStep({
  search,
  onSearchChange,
  loading,
  properties,
  onSelect,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  loading: boolean;
  properties: EstatePropertyOption[];
  onSelect: (p: EstatePropertyOption) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-4 pb-6">
      <label className="relative block">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
          strokeWidth={1.5}
        />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search properties…"
          className="w-full rounded-md bg-card hairline py-2.5 pl-10 pr-3 text-body text-ink focus-visible:border-primary-500 focus-visible:outline-none"
          autoFocus
        />
      </label>

      {loading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-14 rounded-xl" />
          <Skeleton className="h-14 rounded-xl" />
        </div>
      ) : properties.length === 0 ? (
        <p className="py-8 text-center text-body-sm text-ink-muted">
          {search.trim() ? 'No properties match your search.' : 'Start typing to search the estate.'}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {properties.map((p) => (
            <li key={p.location_id}>
              <Card asChild noGrain className="rounded-xl px-4 py-3 text-left shadow-none transition-colors active:bg-sunken">
                <button
                  type="button"
                  onClick={() => onSelect(p)}
                  className="w-full"
                >
                  <p className="font-display text-headline text-ink">{p.name}</p>
                  {p.type_name ? (
                    <p className="text-caption text-ink-muted">{p.type_name}</p>
                  ) : null}
                  {p.address ? (
                    <p className="mt-0.5 text-body-sm text-ink-secondary line-clamp-2">{p.address}</p>
                  ) : null}
                </button>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RelationshipStep({
  relationshipTypes,
  loading,
  relationshipTypeId,
  onRelationshipTypeId,
  residencyKind,
  onResidencyKind,
  onBack,
  onNext,
  submitting,
  skipDocuments,
}: {
  relationshipTypes: PropertyRelationshipTypeOption[];
  loading: boolean;
  relationshipTypeId: string;
  onRelationshipTypeId: (id: string) => void;
  residencyKind: PropertyResidencyKind;
  onResidencyKind: (k: PropertyResidencyKind) => void;
  onBack: () => void;
  onNext: () => void;
  submitting: boolean;
  skipDocuments: boolean;
}): JSX.Element {
  const hasRelationshipTypes = relationshipTypes.length > 0;
  const canProceed = hasRelationshipTypes && !!relationshipTypeId && !submitting;

  return (
    <div className="flex flex-col gap-5 pb-6">
      <fieldset>
        <legend className="eyebrow text-ink-muted">Relationship to property</legend>
        {loading ? (
          <Skeleton className="mt-2 h-10" />
        ) : relationshipTypes.length === 0 ? (
          <div className="mt-2 rounded-lg bg-warning/10 px-4 py-3">
            <p className="text-body-sm text-ink">No relationship types configured for this estate yet.</p>
            <p className="mt-1 text-body-sm text-ink-secondary">
              Ask the tenant team to add one under Properties settings before submitting a claim.
            </p>
          </div>
        ) : (
          <div className="mt-2 flex flex-col gap-2">
            {relationshipTypes.map((r) => {
              const on = relationshipTypeId === r.relationship_type_id;
              return (
                <button
                  key={r.relationship_type_id}
                  type="button"
                  onClick={() => onRelationshipTypeId(r.relationship_type_id)}
                  className={cn(
                    'rounded-xl px-4 py-3 text-left hairline transition-colors',
                    on ? 'bg-primary-50 border-primary-500' : 'bg-card',
                  )}
                >
                  <p className="font-medium text-ink">{r.label}</p>
                  {r.required_documents.length > 0 ? (
                    <p className="mt-0.5 text-caption text-ink-muted">
                      {r.required_documents.length} supporting document
                      {r.required_documents.length === 1 ? '' : 's'} required
                    </p>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </fieldset>

      <fieldset>
        <legend className="eyebrow text-ink-muted">Residency</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {RESIDENCY_OPTIONS.map((opt) => {
            const on = residencyKind === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onResidencyKind(opt.value)}
                className={cn(
                  'rounded-pill px-3 py-1.5 text-caption font-semibold uppercase tracking-wide',
                  on ? 'bg-primary-900 text-white' : 'bg-sunken text-ink-secondary',
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="flex gap-2 pt-2">
        <ActionButton variant="secondary" onClick={onBack}>
          Back
        </ActionButton>
        <ActionButton disabled={!canProceed} loading={submitting} onClick={onNext}>
          {skipDocuments ? 'Submit claim' : 'Continue'}
        </ActionButton>
      </div>
    </div>
  );
}

function DocumentsStep({
  documents,
  uploadedDocIds,
  uploadingDocId,
  onUpload,
  onBack,
  onSubmit,
  submitting,
}: {
  documents: RequiredClaimDocument[];
  uploadedDocIds: Set<string>;
  uploadingDocId: string | null;
  onUpload: (doc: RequiredClaimDocument, file: File | null) => void;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
}): JSX.Element {
  const allUploaded = documents.every((d) => uploadedDocIds.has(d.document_type_id));

  return (
    <div className="flex flex-col gap-4 pb-6">
      <ul className="flex flex-col gap-3">
        {documents.map((doc) => {
          const done = uploadedDocIds.has(doc.document_type_id);
          const busy = uploadingDocId === doc.document_type_id;
          return (
            <Card
              key={doc.document_type_id}
              asChild
              noGrain
              className="rounded-xl px-4 py-3 shadow-none"
            >
              <li>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-ink">{doc.name}</p>
                    <p className="text-caption text-ink-muted">PDF or image, max 10 MB</p>
                  </div>
                  {done ? (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-success/15 text-success">
                      <Check className="h-4 w-4" strokeWidth={2} />
                    </span>
                  ) : (
                    <label className="shrink-0">
                      <input
                        type="file"
                        accept=".pdf,image/jpeg,image/png,image/gif"
                        className="sr-only"
                        disabled={busy}
                        onChange={(e) => {
                          const file = e.target.files?.[0] ?? null;
                          void onUpload(doc, file);
                          e.target.value = '';
                        }}
                      />
                      <span
                        className={cn(
                          'inline-flex h-9 cursor-pointer items-center rounded-pill px-3 text-caption font-semibold uppercase',
                          busy ? 'bg-sunken text-ink-muted' : 'bg-primary-900 text-white',
                        )}
                      >
                        {busy ? 'Uploading…' : 'Upload'}
                      </span>
                    </label>
                  )}
                </div>
              </li>
            </Card>
          );
        })}
      </ul>

      <div className="flex gap-2 pt-2">
        <ActionButton variant="secondary" onClick={onBack}>
          Back
        </ActionButton>
        <ActionButton loading={submitting} disabled={!allUploaded} onClick={onSubmit}>
          Submit claim
        </ActionButton>
      </div>
    </div>
  );
}
